import { WORKFLOW_PATH_LABEL_MAX } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Input } from "@sagot/ui";
import { useEffect, useRef, useState } from "react";

interface PathNameInputProps {
  /** "Path C": names the field for assistive technology. */
  pathTitle: string;
  initial: string;
  /** Enter, or leaving the field: the name as typed (empty removes the name). */
  onSave: (name: string) => void;
  /** Escape. */
  onCancel: () => void;
  className?: string;
}

/** The path's name as a text field in its header: Enter saves, Escape cancels, leaving the field saves (WF-P5-12). */
export function PathNameInput({ pathTitle, initial, onSave, onCancel, className }: PathNameInputProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initial);
  const finished = useRef(false);
  const field = useRef<HTMLInputElement>(null);
  // The field opens ready to type over the old name.
  useEffect(() => {
    field.current?.focus();
    field.current?.select();
  }, []);
  const finish = (save: boolean) => {
    if (finished.current) return;
    finished.current = true;
    if (save) onSave(value);
    else onCancel();
  };
  return (
    <Input
      ref={field}
      aria-label={t("engage.workflows.extras.paths.nameLabel", { path: pathTitle })}
      placeholder={t("engage.workflows.extras.paths.namePlaceholder")}
      maxLength={WORKFLOW_PATH_LABEL_MAX}
      className={className}
      value={value}
      onChange={(event) => { setValue(event.target.value); }}
      onBlur={() => { finish(true); }}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          finish(true);
        } else if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          finish(false);
        }
      }}
    />
  );
}
